// GameSanctuary.jsx - طراحی الهام‌گرفته از Dragon's Grove با تمام قابلیت‌ها

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { Environment, OrbitControls } from '@react-three/drei';

import ModelLoader from '../ModelLoader';
import './GameSanctuary.css';

// --- داده‌های اولیه حیوانات ---
const INITIAL_PETS = [
  { id: 1, name: 'اژدهای ریاضی', level: 5, locked: false, emoji: '🐉', rarity: 'طلایی', power: 85, xp: 0, accessories: [], modelPath: '/models/fox-1-1.glb' },
  { id: 2, name: 'جغد ادبیات', level: 3, locked: false, emoji: '🦉', rarity: 'نقره‌ای', power: 60, xp: 0, accessories: [], modelPath: '/models/fox-1-2.glb' }, // ← اضافه شد
  { id: 3, name: 'تک‌شاخ زبان', level: 1, locked: true, emoji: '🦄', rarity: 'افسانه‌ای', power: 95, xp: 0, accessories: [], modelPath: '/models/fox-1-1.glb' }, // ← اضافه شد
  { id: 4, name: 'ربات علوم', level: 1, locked: true, emoji: '🤖', rarity: 'کمیاب', power: 70, xp: 0, accessories: [], modelPath: '/models/fox-1-1.glb' }, // ← اضافه شد
  { id: 5, name: 'فینیکس تاریخ', level: 1, locked: true, emoji: '🔥', rarity: 'افسانه‌ای', power: 100, xp: 0, accessories: [], modelPath: '/models/fox-1-1.glb' }, // ← اضافه شد
  { id: 6, name: 'نهنگ فضایی', level: 1, locked: true, emoji: '🐋', rarity: 'افسانه‌ای', power: 110, xp: 0, accessories: [], modelPath: '/models/fox-1-1.glb' }, // ← اضافه شد
];

const ACCESSORIES_LIST = ['🎩 کلاه جادوگر', '🕶️ عینک آفتابی', '👑 تاج طلایی', '🧣 شال ابریشمی', '💎 گردنبند ستاره‌ای'];
const RANDOM_NAMES = ['اژدهای یخی', 'گرگ نقره‌ای', 'عنقای طلایی', 'ببر آتشین', 'شاهین کهکشانی', 'کوسه مهتابی'];
const RANDOM_EMOJIS = ['🐲', '🐺', '🦅', '🐯', '🦅', '🦈'];
const RARITY_TIERS = ['افسانه‌ای', 'کمیاب', 'طلایی', 'نقره‌ای'];

const GameSanctuary = () => {
  // ============================================================
  // STATE
  // ============================================================
  const [pets, setPets] = useState(() => {
    const saved = localStorage.getItem('sanctuary_pets');
    return saved ? JSON.parse(saved) : INITIAL_PETS;
  });
  const [selectedPetId, setSelectedPetId] = useState(() => {
    const saved = localStorage.getItem('sanctuary_selected');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [coins, setCoins] = useState(() => {
    const saved = localStorage.getItem('sanctuary_coins');
    return saved ? parseInt(saved, 10) : 2840;
  });
  const [userLevel, setUserLevel] = useState(() => {
    const saved = localStorage.getItem('sanctuary_userLevel');
    return saved ? parseInt(saved, 10) : 12;
  });
  const [showBattle, setShowBattle] = useState(false);
  const [battleData, setBattleData] = useState(null);
  const [battleResult, setBattleResult] = useState(null);
  const [notification, setNotification] = useState(null);
  const [shopOpen, setShopOpen] = useState(false);
  const [dailyMissions, setDailyMissions] = useState(() => {
    const saved = localStorage.getItem('sanctuary_missions');
    if (saved) {
      const parsed = JSON.parse(saved);
      const today = new Date().toDateString();
      if (parsed.date === today) return parsed.missions;
    }
    return [
      { id: 'm1', label: '🍖 ۳ بار غذا بده', done: false, reward: 30, target: 3, progress: 0 },
      { id: 'm2', label: '🤚 ۵ بار نوازش کن', done: false, reward: 20, target: 5, progress: 0 },
      { id: 'm3', label: '⚔️ یک مبارزه کن', done: false, reward: 50, target: 1, progress: 0 },
      { id: 'm4', label: '⬆ یک حیوان را ارتقا بده', done: false, reward: 40, target: 1, progress: 0 },
    ];
  });
  const [achievements, setAchievements] = useState(() => {
    const saved = localStorage.getItem('sanctuary_achievements');
    return saved ? JSON.parse(saved) : [];
  });
  const [inventory, setInventory] = useState(() => {
    const saved = localStorage.getItem('sanctuary_inventory');
    return saved ? JSON.parse(saved) : { accessories: [] };
  });

  const notifTimeoutRef = useRef(null);

  // ============================================================
  // PERSISTENCE
  // ============================================================
  useEffect(() => {
    localStorage.setItem('sanctuary_pets', JSON.stringify(pets));
  }, [pets]);
  useEffect(() => {
    localStorage.setItem('sanctuary_selected', String(selectedPetId));
  }, [selectedPetId]);
  useEffect(() => {
    localStorage.setItem('sanctuary_coins', String(coins));
  }, [coins]);
  useEffect(() => {
    localStorage.setItem('sanctuary_userLevel', String(userLevel));
  }, [userLevel]);
  useEffect(() => {
    localStorage.setItem(
      'sanctuary_missions',
      JSON.stringify({ date: new Date().toDateString(), missions: dailyMissions })
    );
  }, [dailyMissions]);
  useEffect(() => {
    localStorage.setItem('sanctuary_achievements', JSON.stringify(achievements));
  }, [achievements]);
  useEffect(() => {
    localStorage.setItem('sanctuary_inventory', JSON.stringify(inventory));
  }, [inventory]);

  // ============================================================
  // SELECTED PET
  // ============================================================
  const selectedPet = useMemo(
    () => pets.find((p) => p.id === selectedPetId),
    [pets, selectedPetId]
  );

  // ============================================================
  // NOTIFICATION SYSTEM
  // ============================================================
  const showNotification = useCallback((message, type = 'info') => {
    setNotification({ message, type });
    clearTimeout(notifTimeoutRef.current);
    notifTimeoutRef.current = setTimeout(() => setNotification(null), 2800);
  }, []);

  // ============================================================
  // MISSION TRACKING
  // ============================================================
  const trackMission = useCallback(
    (id, progressIncrement = 1) => {
      setDailyMissions((prev) =>
        prev.map((m) => {
          if (m.id === id && !m.done) {
            const newProgress = Math.min(m.target, m.progress + progressIncrement);
            const done = newProgress >= m.target;
            if (done) {
              setCoins((c) => c + m.reward);
              showNotification(`✅ ماموریت "${m.label}" کامل شد! +${m.reward} سکه`, 'success');
              checkAchievements();
            }
            return { ...m, progress: newProgress, done };
          }
          return m;
        })
      );
    },
    [showNotification]
  );

  // ============================================================
  // ACHIEVEMENTS
  // ============================================================
  const checkAchievements = useCallback(() => {
    const unlockedCount = pets.filter((p) => !p.locked).length;
    const totalLevels = pets.reduce((sum, p) => sum + p.level, 0);
    const newAchievements = [];

    if (unlockedCount >= 3 && !achievements.includes('collector_3')) {
      newAchievements.push('collector_3');
      showNotification('🏆 دستاورد: کلکسیون‌دار ۳ حیوان!', 'info');
    }
    if (unlockedCount >= 6 && !achievements.includes('collector_6')) {
      newAchievements.push('collector_6');
      showNotification('🏆 دستاورد: کلکسیون‌دار ۶ حیوان!', 'info');
    }
    if (totalLevels >= 20 && !achievements.includes('level_20')) {
      newAchievements.push('level_20');
      showNotification('🏆 دستاورد: مجموع سطح ۲۰!', 'info');
    }
    if (coins >= 5000 && !achievements.includes('rich_5000')) {
      newAchievements.push('rich_5000');
      showNotification('🏆 دستاورد: ثروتمند (۵۰۰۰ سکه)!', 'info');
    }
    if (newAchievements.length > 0) {
      setAchievements((prev) => [...prev, ...newAchievements]);
    }
  }, [pets, achievements, coins, showNotification]);

  // ============================================================
  // CORE FUNCTIONS
  // ============================================================
  const handleUnlock = useCallback(
    (id) => {
      const pet = pets.find((p) => p.id === id);
      if (!pet || !pet.locked) return;
      if (coins >= 500) {
        setCoins((prev) => prev - 500);
        setPets((prev) =>
          prev.map((p) =>
            p.id === id ? { ...p, locked: false, level: 1, xp: 0 } : p
          )
        );
        showNotification(`🎉 ${pet.name} به جمع پیوست!`, 'success');
        checkAchievements();
      } else {
        showNotification('❌ ۵۰۰ سکه نیاز داری!', 'error');
      }
    },
    [coins, pets, showNotification, checkAchievements]
  );

  const handleFeed = useCallback(() => {
    if (!selectedPet || selectedPet.locked) {
      showNotification('🔒 اول این حیوان رو باز کن!', 'error');
      return;
    }
    const xpGain = 8 + Math.floor(Math.random() * 7);
    const coinGain = 5 + Math.floor(Math.random() * 10);

    setCoins((prev) => prev + coinGain);
    setPets((prev) =>
      prev.map((p) => {
        if (p.id !== selectedPet.id) return p;
        let newXp = p.xp + xpGain;
        let newLevel = p.level;
        let newPower = p.power;
        if (newXp >= 100) {
          newXp = 0;
          newLevel += 1;
          newPower += 15;
          showNotification(`🎉 ${p.name} به سطح ${newLevel} رسید!`, 'success');
          setUserLevel((lvl) => lvl + 1);
          checkAchievements();
        }
        return { ...p, xp: newXp, level: newLevel, power: newPower };
      })
    );
    trackMission('m1');
    showNotification(`🍖 ${selectedPet.name} غذا خورد! +${coinGain} سکه`, 'coin');
  }, [selectedPet, showNotification, trackMission, checkAchievements]);

  const handlePet = useCallback(() => {
    if (!selectedPet || selectedPet.locked) {
      showNotification('🔒 اول این حیوان رو باز کن!', 'error');
      return;
    }
    setCoins((prev) => prev + 3);
    trackMission('m2');
    showNotification('🤚 نوازش شد! +۳ سکه', 'coin');
  }, [selectedPet, showNotification, trackMission]);

  const handleUpgrade = useCallback(() => {
    if (!selectedPet || selectedPet.locked) {
      showNotification('🔒 اول این حیوان رو باز کن!', 'error');
      return;
    }
    const cost = 300 + selectedPet.level * 50;
    if (coins >= cost) {
      setCoins((prev) => prev - cost);
      setPets((prev) =>
        prev.map((p) =>
          p.id === selectedPet.id
            ? { ...p, level: p.level + 1, power: p.power + 20, xp: 0 }
            : p
        )
      );
      trackMission('m4');
      showNotification(`⬆ ${selectedPet.name} ارتقا یافت!`, 'success');
      checkAchievements();
    } else {
      showNotification(`❌ به ${cost} سکه نیاز داری!`, 'error');
    }
  }, [selectedPet, coins, showNotification, trackMission, checkAchievements]);

  const handleCosmetic = useCallback(() => {
    if (!selectedPet || selectedPet.locked) {
      showNotification('🔒 اول این حیوان رو باز کن!', 'error');
      return;
    }
    if (inventory.accessories.length === 0) {
      showNotification('❌ هیچ اکسسوری در انبار نداری! از فروشگاه بخر.', 'error');
      return;
    }
    const item = inventory.accessories[0];
    setInventory((prev) => ({
      ...prev,
      accessories: prev.accessories.slice(1),
    }));
    setPets((prev) =>
      prev.map((p) =>
        p.id === selectedPet.id
          ? { ...p, accessories: [...p.accessories, item] }
          : p
      )
    );
    showNotification(`✨ ${item} به ${selectedPet.name} اضافه شد!`, 'info');
  }, [selectedPet, inventory, showNotification]);

  const handleBattle = useCallback(() => {
    if (!selectedPet || selectedPet.locked) {
      showNotification('🔒 یک حیوان باز شده انتخاب کن!', 'error');
      return;
    }
    const opponents = pets.filter((p) => !p.locked && p.id !== selectedPet.id);
    if (opponents.length === 0) {
      showNotification('❌ حریفی برای مبارزه نیست!', 'error');
      return;
    }
    const opponent = opponents[Math.floor(Math.random() * opponents.length)];
    setBattleData({ myPet: selectedPet, oppPet: opponent });
    setBattleResult(null);
    setShowBattle(true);

    setTimeout(() => {
      const myPower = selectedPet.power + selectedPet.level * 6;
      const oppPower = opponent.power + opponent.level * 6;
      const myWin = myPower + Math.random() * 35 > oppPower + Math.random() * 35;
      const winner = myWin ? selectedPet : opponent;
      const loser = myWin ? opponent : selectedPet;
      const coinReward = myWin ? 60 + Math.floor(Math.random() * 40) : 15;
      setBattleResult({ winner, loser, coinReward });
      trackMission('m3');
    }, 2200);
  }, [selectedPet, pets, showNotification, trackMission]);

  const closeBattle = useCallback(() => {
    if (battleResult) {
      const { winner, loser, coinReward } = battleResult;
      setCoins((prev) => prev + coinReward);
      if (winner.id === selectedPet?.id) {
        const xpGain = 18;
        setPets((prev) =>
          prev.map((p) =>
            p.id === selectedPet.id
              ? { ...p, xp: Math.min(100, p.xp + xpGain) }
              : p
          )
        );
        showNotification(`⚔️ ${winner.name} پیروز شد! +${xpGain}XP`, 'success');
        checkAchievements();
      } else {
        showNotification(`💔 ${loser.name} شکست خورد!`, 'error');
      }
      showNotification(`🏆 ${winner.name} پیروز شد! +${coinReward} سکه`, 'coin');
    }
    setShowBattle(false);
    setBattleData(null);
    setBattleResult(null);
  }, [battleResult, selectedPet, showNotification, checkAchievements]);

  const handleChest = useCallback(() => {
    if (coins >= 1000) {
      setCoins((prev) => prev - 1000);
      const randomIndex = Math.floor(Math.random() * RANDOM_NAMES.length);
      const newPet = {
        id: Date.now() + Math.random(),
        name: RANDOM_NAMES[randomIndex],
        level: 1,
        locked: false,
        emoji: RANDOM_EMOJIS[randomIndex % RANDOM_EMOJIS.length],
        rarity: RARITY_TIERS[Math.floor(Math.random() * RARITY_TIERS.length)],
        power: 80 + Math.floor(Math.random() * 50),
        xp: 0,
        accessories: [],
      };
      setPets((prev) => [...prev, newPet]);
      showNotification(`🎁 ${newPet.name} از صندوق بیرون آمد!`, 'success');
      checkAchievements();
    } else {
      showNotification('❌ به ۱۰۰۰ سکه نیاز داری!', 'error');
    }
  }, [coins, showNotification, checkAchievements]);

  const handleShopBuy = useCallback(
    (item, cost) => {
      if (coins >= cost) {
        setCoins((prev) => prev - cost);
        setInventory((prev) => ({
          ...prev,
          accessories: [...prev.accessories, item],
        }));
        showNotification(`🛒 ${item} خریداری شد!`, 'success');
      } else {
        showNotification(`❌ به ${cost} سکه نیاز داری!`, 'error');
      }
    },
    [coins, showNotification]
  );

  // ============================================================
  // RIPPLE EFFECT
  // ============================================================
  const createRipple = useCallback((e) => {
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const ripple = document.createElement('span');
    ripple.className = 'btn-ripple';
    const size = Math.max(rect.width, rect.height);
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
    ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
    btn.appendChild(ripple);
    setTimeout(() => ripple.remove(), 700);
  }, []);

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="game-sanctuary-app">
      <div className="sanctuary-container">
        {/* --- Notification --- */}
        {notification && (
          <div className={`notification ${notification.type}`}>{notification.message}</div>
        )}

        {/* --- Header --- */}
        <header className="game-header">
          <div className="header-glow" />
          <div className="user-info">
            <div className="avatar-ring">
              <span className="avatar">🧑‍🎓</span>
              <div className="avatar-status" />
            </div>
            <div className="user-text">
              <h3>آریا سلطانی</h3>
              <span>
                لول {userLevel} • استاد ارشد • {pets.filter((p) => !p.locked).length} حیوان
              </span>
            </div>
          </div>
          <div className="header-stats">
            <div
              className="coin-display"
              onClick={() => showNotification(`💰 ${coins.toLocaleString()} سکه`, 'info')}
            >
              <span className="coin-icon">🪙</span>
              <span className="coin-value">{coins.toLocaleString()}</span>
            </div>
            <div className="xp-section">
              <span className="xp-label">XP</span>
              <div className="xp-bar-bg">
                <div className="xp-bar-fill" style={{ width: `${selectedPet?.xp ?? 0}%` }}>
                  <div className="xp-glow" />
                </div>
              </div>
              <span className="xp-text">{selectedPet?.xp ?? 0}%</span>
            </div>
            <button className="shop-toggle" onClick={() => setShopOpen((s) => !s)}>
              🏪
            </button>
          </div>
        </header>

        {/* --- Main --- */}
        <main className="main-stage">
          {/* Pet Showcase */}
          <div className="pet-showcase card">
            <div className="scene-container">
  <div className="pet-3d-display">
    {/* هاله‌های طلایی (همون افکت قبلی) */}
    <div className="pet-aura" />
    <div className="pet-aura-2" />
    
    {/* ========== کانواس سه‌بعدی ========== */}
    <Canvas
  camera={{ position: [0, 1.5, 4], fov: 45 }}
  style={{ 
    width: '100%', 
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 2,
    background: '#d4ccc4'
  }}
  gl={{ alpha: true }}
>
  <ambientLight intensity={1.2} />
  <directionalLight position={[5, 10, 5]} intensity={2} />
  <directionalLight position={[-5, 5, 5]} intensity={1} />
  <pointLight position={[0, 3, 5]} intensity={0.5} />
  
  <Environment preset="city" />
  
  <ModelLoader 
    modelPath={selectedPet?.modelPath || ""}
    scale={2.5}
  />
  
  <OrbitControls enableZoom={true} enablePan={true} />
</Canvas>
    {/* ================================== */}
    
    {/* برچسب سطح و نام (همون pet-level-tag) */}
    <div className="pet-level-tag">
      <span>⚡ سطح {selectedPet?.level || 0}</span>
      <span className="pet-name">{selectedPet?.name || '—'}</span>
      <span>💪 {selectedPet ? selectedPet.power + selectedPet.level * 6 : 0}</span>
      {selectedPet?.accessories?.length > 0 && (
        <span className="accessory-badge">✨ {selectedPet.accessories.length}</span>
      )}
    </div>
  </div>
</div>

            <div className="action-bar">
              <button className="action-btn primary" onClick={handleFeed} onMouseDown={createRipple}>
                <span>🍖</span> غذا
              </button>
              <button className="action-btn secondary" onClick={handlePet} onMouseDown={createRipple}>
                <span>🤚</span> نوازش
              </button>
              <button className="action-btn upgrade" onClick={handleUpgrade} onMouseDown={createRipple}>
                <span>⬆</span> ارتقا
              </button>
              <button className="action-btn cosmetic" onClick={handleCosmetic} onMouseDown={createRipple}>
                <span>✨</span> اکسسوری
              </button>
              <button className="action-btn battle" onClick={handleBattle} onMouseDown={createRipple}>
                <span>⚔️</span> مبارزه
              </button>
            </div>
          </div>

          {/* Collection */}
          <div className="showcase-wrapper card">
            <div className="showcase-header">
              <h4>🏰 کلکسیون حیوانات</h4>
              <span className="showcase-counter">
                {pets.filter((p) => !p.locked).length}/{pets.length}
              </span>
            </div>
            <div className="pet-shelf">
              {pets.map((pet) => (
                <div
                  key={pet.id}
                  className={`shelf-item ${pet.locked ? 'locked' : 'unlocked'} ${
                    selectedPetId === pet.id ? 'active' : ''
                  }`}
                  onClick={() => !pet.locked && setSelectedPetId(pet.id)}
                >
                  <div className="shelf-card">
                    <div className="shelf-emoji">{pet.locked ? '🔒' : pet.emoji}</div>
                    {pet.locked ? (
                      <>
                        <div className="lock-overlay">🔒</div>
                        <button
                          className="unlock-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnlock(pet.id);
                          }}
                        >
                          🪙 باز کردن
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="shelf-name">{pet.name}</span>
                        <span className="shelf-level">Lv.{pet.level}</span>
                        <div className={`rarity-badge ${pet.rarity}`}>{pet.rarity}</div>
                        {pet.accessories?.length > 0 && (
                          <span className="shelf-accessory">✨{pet.accessories.length}</span>
                        )}
                      </>
                    )}
                  </div>
                  {selectedPetId === pet.id && !pet.locked && (
                    <div className="active-indicator">▶</div>
                  )}
                </div>
              ))}
            </div>
            {/* Missions */}
            <div className="missions-mini">
              <h5>📋 ماموریت‌های روزانه</h5>
              <div className="mission-list">
                {dailyMissions.map((m) => (
                  <div key={m.id} className={`mission-item ${m.done ? 'done' : ''}`}>
                    <span>{m.label}</span>
                    <span className="mission-progress">
                      {m.done ? '✅' : `${m.progress}/${m.target}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>

        {/* --- Footer --- */}
        <footer className="weekly-footer card">
          <div className="chest-container">
            <div className="mystery-chest">
              <span>🎁</span>
              <span className="chest-glow" />
            </div>
            <div className="chest-text">
              <h4>📦 صندوق اسرارآمیز هفتگی</h4>
              <p>با ۱۰۰۰ سکه یک حیوان نادر + اکسسوری ویژه دریافت کن!</p>
            </div>
            <button className="chest-btn" onClick={handleChest} onMouseDown={createRipple}>
              🗝️ باز کردن
            </button>
          </div>
        </footer>

        {/* --- Shop Modal --- */}
        {shopOpen && (
          <div className="shop-modal" onClick={() => setShopOpen(false)}>
            <div className="shop-content" onClick={(e) => e.stopPropagation()}>
              <h2>🏪 فروشگاه</h2>
              <div className="shop-items">
                {ACCESSORIES_LIST.map((item, idx) => {
                  const cost = 200 + idx * 50;
                  return (
                    <div key={idx} className="shop-item">
                      <span>{item}</span>
                      <span className="shop-price">🪙 {cost}</span>
                      <button
                        className="shop-buy-btn"
                        onClick={() => handleShopBuy(item, cost)}
                      >
                        خرید
                      </button>
                    </div>
                  );
                })}
              </div>
              <button className="shop-close" onClick={() => setShopOpen(false)}>
                ✖
              </button>
            </div>
          </div>
        )}

        {/* --- Battle Modal --- */}
        {showBattle && (
          <div className="battle-modal" onClick={closeBattle}>
            <div className="battle-content" onClick={(e) => e.stopPropagation()}>
              <h2>⚔️ نبرد سینمایی</h2>
              <div className="battle-arena">
                <div className="battle-pet">
                  {battleData?.myPet?.emoji || '❓'}
                  <div className="battle-pet-name">
                    {battleData?.myPet?.name || '—'} 💪
                    {battleData?.myPet
                      ? battleData.myPet.power + battleData.myPet.level * 6
                      : 0}
                  </div>
                </div>
                <div className="battle-vs">VS</div>
                <div className="battle-pet">
                  {battleResult ? (
                    battleResult.loser.emoji
                  ) : (
                    battleData?.oppPet?.emoji || '❓'
                  )}
                  <div className="battle-pet-name">
                    {battleResult ? battleResult.loser.name : battleData?.oppPet?.name || 'حریف...'}
                  </div>
                </div>
              </div>
              {battleResult ? (
                <div className="battle-result">
                  <div className="battle-winner">
                    🏆 <span className="winner-name">{battleResult.winner.name}</span> پیروز شد!
                  </div>
                  <div className="battle-reward">
                    🪙 +{battleResult.coinReward} سکه
                  </div>
                  <button
                    onClick={closeBattle}
                    className="battle-continue-btn"
                  >
                    ادامه 🚀
                  </button>
                </div>
              ) : (
                <div className="battle-loading">
                  ⏳ در حال نبرد...
                  <div className="battle-loading-icon">⚡</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GameSanctuary;